"use client";
import React from "react";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";

export default function PrivacyPolicy() {
  return (
    <>
      <div
        className="w-full py-16 px-6 md:px-12"
        style={{
          backgroundColor: "var(--nhs-pale-grey)",
          color: "var(--nhs-black)",
        }}
      >
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center mb-8 px-4 py-3 font-bold no-underline"
            style={{
              color: "var(--nhs-dark-blue)",
              backgroundColor: "var(--nhs-white)",
              border: "2px solid var(--nhs-dark-blue)",
              boxShadow: "0 3px 0 var(--nhs-dark-blue)",
            }}
          >
            <span aria-hidden="true" className="mr-2">
              ←
            </span>
            Back to home
          </Link>

          <h1
            className="text-4xl font-bold mb-2"
            style={{ color: "var(--nhs-dark-blue)" }}
          >
            Privacy Policy
          </h1>

          <p className="text-sm mb-6" style={{ color: "var(--nhs-dark-grey)" }}>
            Last updated: 29 August 2026
          </p>

          <p className="text-lg mb-8">
            This Privacy Policy explains how Prince George University Hospital
            (PGUH) collects, uses, stores, and protects personal information
            when you use our website, services, applications, or staff systems.
            We handle personal information in accordance with applicable UK
            data protection law, including the UK General Data Protection
            Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Information we collect</h2>

            <p className="mb-4">
              Depending on which services you use, we may collect and process
              the following information:
            </p>

            <ul className="list-disc pl-6 space-y-2">
              <li>Email addresses</li>
              <li>Roblox usernames</li>
              <li>Roblox user IDs</li>
              <li>Discord usernames</li>
              <li>Discord user IDs</li>
              <li>
                Application data, including answers submitted through staff,
                role, department, or other application forms
              </li>
              <li>
                Account information associated with your PGUH account
              </li>
              <li>
                Authentication information, including securely stored password
                hashes where password-based authentication is used
              </li>
              <li>
                IP addresses and related network information processed through
                Cloudflare
              </li>
              <li>
                Security and technical information required to protect our
                website, accounts, and services
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">
              Roblox and Discord information
            </h2>

            <p className="mb-4">
              Where Roblox or Discord information is provided or connected to
              an account, we may use identifiers such as your username and user
              ID to identify your account, verify your identity, manage staff
              roles, process applications, and provide access to PGUH services.
            </p>

            <p>
              Roblox and Discord user IDs may continue to be used internally
              even if your visible username changes because these identifiers
              are generally unique to your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Application data</h2>

            <p>
              When you submit an application, we may collect the information
              you provide within that application. This may include your
              answers to application questions, Roblox or Discord account
              details, the role you are applying for, application status,
              reviewer decisions, and information required to assess or manage
              your application.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">
              Passwords and authentication
            </h2>

            <p>
              Where password-based authentication is available, passwords are
              used to authenticate your account. Passwords should not be stored
              by PGUH in readable plain-text form. Authentication providers may
              instead securely process and store cryptographic password hashes
              and other authentication information required to operate your
              account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">
              IP addresses and Cloudflare
            </h2>

            <p className="mb-4">
              Our website may use Cloudflare to provide security, performance,
              content delivery, and protection against malicious traffic.
              When you connect to the website, Cloudflare may process technical
              information including your IP address and information relating to
              your connection or request.
            </p>

            <p>
              This information may be used for purposes such as preventing
              abuse, detecting suspicious activity, mitigating attacks,
              enforcing security controls, and maintaining the availability
              and integrity of our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">How we use information</h2>

            <ul className="list-disc pl-6 space-y-2">
              <li>Creating and managing user accounts</li>
              <li>Staff and applicant communication</li>
              <li>Processing and reviewing applications</li>
              <li>Application and role management</li>
              <li>Verifying Roblox and Discord identities</li>
              <li>Managing staff permissions and access</li>
              <li>Account authentication and verification</li>
              <li>Preventing fraud, abuse, and unauthorised access</li>
              <li>Investigating security incidents</li>
              <li>Maintaining the security and reliability of our services</li>
              <li>Complying with applicable legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Legal basis</h2>

            <p>
              Personal data may be processed under one or more lawful bases
              recognised by UK GDPR, including legitimate interests,
              contractual necessity, consent where appropriate, and compliance
              with legal obligations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">
              Third-party service providers
            </h2>

            <p className="mb-4">
              We may use third-party service providers to operate parts of our
              website and services. These providers may process limited
              personal information where necessary to provide their services.
            </p>

            <p>
              This may include infrastructure, authentication, database,
              hosting, security, email, Roblox, Discord, and content delivery
              providers such as Cloudflare.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Data security</h2>

            <p>
              PGUH applies appropriate technical and organisational measures
              designed to protect personal data against unauthorised access,
              alteration, disclosure, loss, destruction, or misuse. Access to
              personal information is restricted where appropriate to
              authorised users who require it for legitimate purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Data retention</h2>

            <p>
              Personal information is retained only for as long as reasonably
              necessary for the purpose for which it was collected, including
              account management, application processing, security,
              record-keeping, and legal requirements. Information that is no
              longer required may be securely deleted or anonymised.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Your rights</h2>

            <p className="mb-4">
              Depending on the circumstances, you may have rights under UK data
              protection law relating to your personal information, including:
            </p>

            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>The right to access your personal information</li>
              <li>The right to request correction of inaccurate information</li>
              <li>The right to request deletion of your information</li>
              <li>The right to request restriction of processing</li>
              <li>The right to object to certain processing</li>
              <li>The right to data portability where applicable</li>
            </ul>

            <p>
              Additional information about how PGUH handles personal data and
              data protection requests may be provided within our GDPR & Data
              Protection Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4">Contact</h2>

            <p>
              Privacy-related enquiries or requests relating to your personal
              information can be sent to{" "}
              <a
                href="mailto:pguh.co.uk@gmail.com"
                className="font-semibold underline"
                style={{ color: "var(--nhs-dark-blue)" }}
              >
                pguh.co.uk@gmail.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </>
  );
}
